import React, { useState, useEffect } from 'react';
import { FileText, Clock, DollarSign, Briefcase, Plus, X, Upload } from 'lucide-react';

const API = 'https://talentflow-backend-production-09dd.up.railway.app/api';

export default function LiveJobs({ token }) {
  const [jobs, setJobs] = useState([]);
  const [selected, setSelected] = useState(null);
  const [form, setForm] = useState({ name:'', email:'', phone:'', experience:'', skills:'', expectedRate:'', rateType:'same_budget', vendorRate:'', availability:'immediate', noticePeriod:'', summary:'' });
  const [msg, setMsg] = useState('');
  const [msgType, setMsgType] = useState('success');
  const [loading, setLoading] = useState(false);
  const [showBenchModal, setShowBenchModal] = useState(false);
  const [benchJob, setBenchJob] = useState(null);
  const [benchList, setBenchList] = useState([]);
  const [selectedBench, setSelectedBench] = useState([]);
  const [submittingBench, setSubmittingBench] = useState(false);
  const [cvFile, setCvFile] = useState(null);
  const [cvText, setCvText] = useState('');
  const [cvParsing, setCvParsing] = useState(false);
  const [cvParsed, setCvParsed] = useState(false);
  const [step, setStep] = useState(1); // 1=upload CV, 2=verify form, 3=done
  const [interviewStatus, setInterviewStatus] = useState(null); // null, 'checking', 'done', 'pending'

  useEffect(() => { loadJobs(); }, []);

  const loadJobs = async () => {
    try {
      const r = await fetch(`${API}/requirements/live`, { headers: { Authorization: `Bearer ${token}` } });
      const d = await r.json();
      if (d.success) setJobs(d.requirements || []);
    } catch {}
  };

  const handleCvUpload = async (file) => {
    if (!file) return;
    setCvFile(file);
    setCvParsing(true);
    setCvParsed(false);
    try {
      const fd = new FormData();
      fd.append('cv', file);
      const r = await fetch(`${API}/cv/parse`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` },
        body: fd
      });
      const d = await r.json();
      if (d.success && d.data) {
        const p = d.data;
        if (d.cvText) setCvText(d.cvText);
        setForm(prev => ({
          ...prev,
          name: p.name || prev.name,
          email: p.email || prev.email,
          phone: p.phone || prev.phone,
          experience: p.experience ? String(p.experience) : prev.experience,
          skills: p.skills?.length > 0 ? p.skills.join(', ') : prev.skills,
          noticePeriod: p.noticePeriod || prev.noticePeriod,
        }));
        setCvParsed(true);
        // Check interview status by email
        if (p.email) checkInterviewStatus(p.email, p.phone);
        setStep(2);
      } else {
        setStep(2); // Go to form even if parse fails
      }
    } catch {
      setStep(2);
    }
    setCvParsing(false);
  };

  const checkInterviewStatus = async (email, phone) => {
    setInterviewStatus('checking');
    try {
      const r = await fetch(`${API}/interview/check-status?email=${encodeURIComponent(email)}&phone=${encodeURIComponent(phone || '')}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      const d = await r.json();
      if (d.success) {
        setInterviewStatus(d.completed ? 'done' : 'pending');
      } else {
        setInterviewStatus('pending');
      }
    } catch {
      setInterviewStatus('pending');
    }
  };

  const submitCandidate = async () => {
    setLoading(true);
    try {
      const r = await fetch(`${API}/candidates`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({
          name: form.name,
          email: form.email,
          phone: form.phone,
          experience: Number(form.experience),
          skills: form.skills.split(',').map(s => s.trim()).filter(Boolean),
          expectedRate: form.rateType === 'same_budget' ? selected?.budget : Number(form.vendorRate || form.expectedRate),
          rateType: form.rateType,
          vendorRate: form.rateType !== 'same_budget' ? Number(form.vendorRate) : selected?.budget,
          availability: form.availability,
          noticePeriod: form.noticePeriod,
          summary: form.summary,
          cvText: cvText,
          requirementId: selected._id,
        })
      });
      const d = await r.json();
      if (d.success) {
        setMsg('✅ Candidate submitted! Admin will review shortly.');
        setMsgType('success');
        setSelected(null);
        resetForm();
      } else {
        setMsg(d.message || 'Error submitting candidate');
        setMsgType('error');
      }
    } catch { setMsg('Network error!'); setMsgType('error'); }
    setLoading(false);
  };

  const resetForm = () => {
    setForm({ name:'', email:'', phone:'', experience:'', skills:'', expectedRate:'', rateType:'same_budget', vendorRate:'', availability:'immediate', noticePeriod:'', summary:'' });
    setCvFile(null); setCvParsed(false); setStep(1); setInterviewStatus(null);
  };

  const openBenchModal = async (job) => {
    try {
      const r = await fetch(`${API}/bench/my`, { headers: { Authorization: `Bearer ${token}` } });
      const d = await r.json();
      if (d.success) setBenchList(d.resources || []);
    } catch {}
    setBenchJob(job);
    setSelectedBench([]);
    setShowBenchModal(true);
  };

  const toggleBenchSelect = (id) => {
    if (selectedBench.includes(id)) setSelectedBench(selectedBench.filter(s => s !== id));
    else if (selectedBench.length < 3) setSelectedBench([...selectedBench, id]);
  };

  const submitFromBench = async () => {
    if (!selectedBench.length) return;
    setSubmittingBench(true);
    let successCount = 0;
    for (const benchId of selectedBench) {
      const bench = benchList.find(b => b._id === benchId);
      if (!bench) continue;
      try {
        const r = await fetch(`${API}/candidates`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
          body: JSON.stringify({
            name: bench.name,
            experience: bench.experience,
            skills: bench.skills,
            expectedRate: bench.rate,
            availability: bench.availability,
            requirementId: benchJob._id,
            summary: bench.summary,
            email: bench.email,
            phone: bench.phone,
          })
        });
        const d = await r.json();
        if (d.success) successCount++;
      } catch {}
    }
    setSubmittingBench(false);
    setShowBenchModal(false);
    setSelectedBench([]);
    alert(`${successCount} candidate(s) submitted successfully!`);
  };

  const openModal = (job) => { setSelected(job); resetForm(); };

  const timeLeft = (deadline) => {
    if (!deadline) return 'No deadline';
    const diff = new Date(deadline) - new Date();
    if (diff <= 0) return 'Expired';
    const hrs = Math.floor(diff / 3600000);
    if (hrs > 24) return `${Math.floor(hrs/24)}d left`;
    return `${hrs}h left`;
  };

  const inp = { width:'100%', padding:'10px 14px', background:'white', border:'1px solid #e2e8f0', borderRadius:'8px', color:'#1e293b', fontSize:'14px', outline:'none', boxSizing:'border-box', fontFamily:'inherit' };

  return (
    <>
    <div>
      {msg && (
        <div style={{ padding:'12px 16px', borderRadius:'10px', marginBottom:'20px', fontSize:'14px',
          background: msgType==='success'?'#dcfce7':'#fee2e2',
          color: msgType==='success'?'#16a34a':'#dc2626',
          border: `1px solid ${msgType==='success'?'#86efac':'#fca5a5'}` }}>
          {msg}
          <button onClick={() => setMsg('')} style={{ float:'right', background:'none', border:'none', cursor:'pointer', color:'inherit' }}><X size={16}/></button>
        </div>
      )}

      {/* Job Cards */}
      <div style={{ display:'grid', gap:'16px' }}>
        {jobs.map(job => (
          <div key={job._id} style={{ background:'white', borderRadius:'12px', padding:'24px', border:'1px solid #e2e8f0', boxShadow:'0 1px 4px rgba(0,0,0,0.04)' }}>
            <div style={{ display:'flex', justifyContent:'space-between', alignItems:'flex-start', marginBottom:'16px' }}>
              <div>
                <h3 style={{ color:'#1e293b', fontSize:'18px', fontWeight:'700', margin:'0 0 8px' }}>{job.title}</h3>
                <div style={{ display:'flex', gap:'16px', flexWrap:'wrap' }}>
                  <span style={{ color:'#64748b', fontSize:'13px', display:'flex', alignItems:'center', gap:'4px' }}><Briefcase size={13}/> {job.experience} yrs exp</span>
                  <span style={{ color:'#64748b', fontSize:'13px', display:'flex', alignItems:'center', gap:'4px' }}><DollarSign size={13}/> Rs.{job.adminBudget || job.budget?.toLocaleString()}/mo</span>
                  <span style={{ color:'#64748b', fontSize:'13px', display:'flex', alignItems:'center', gap:'4px' }}><Clock size={13}/> {timeLeft(job.deadline)}</span>
                </div>
              </div>
              <div style={{display:'flex', gap:'8px'}}>
                <button onClick={() => openModal(job)}
                  style={{ display:'inline-flex', alignItems:'center', gap:'6px', padding:'10px 20px', background:'linear-gradient(135deg,#6366f1,#8b5cf6)', color:'white', border:'none', borderRadius:'8px', cursor:'pointer', fontSize:'13px', fontWeight:'600', fontFamily:'inherit', whiteSpace:'nowrap' }}>
                  <Plus size={14}/> Submit New
                </button>
                <button onClick={() => openBenchModal(job)}
                  style={{ display:'inline-flex', alignItems:'center', gap:'6px', padding:'10px 20px', background:'white', color:'#6366f1', border:'2px solid #6366f1', borderRadius:'8px', cursor:'pointer', fontSize:'13px', fontWeight:'600', fontFamily:'inherit', whiteSpace:'nowrap' }}>
                  From Bench
                </button>
              </div>
            </div>
            {job.skills?.length > 0 && (
              <div style={{ display:'flex', gap:'6px', flexWrap:'wrap', marginBottom:'12px' }}>
                {job.skills.map(s => <span key={s} style={{ padding:'3px 10px', background:'#ede9fe', color:'#6366f1', borderRadius:'4px', fontSize:'12px', fontWeight:'500' }}>{s}</span>)}
              </div>
            )}
            {job.jobDescription && <p style={{ color:'#64748b', fontSize:'14px', lineHeight:'1.6', margin:0 }}>{job.jobDescription}</p>}
          </div>
        ))}
      </div>

      {jobs.length === 0 && (
        <div style={{ textAlign:'center', padding:'60px', background:'white', borderRadius:'12px', border:'1px solid #e2e8f0', color:'#94a3b8' }}>
          <FileText size={48} color="#e2e8f0" style={{ margin:'0 auto 16px', display:'block' }}/>
          <p style={{ margin:0, fontSize:'15px' }}>No live jobs available right now</p>
        </div>
      )}

      {/* Modal */}
      {selected && (
        <div style={{ position:'fixed', inset:0, background:'rgba(0,0,0,0.6)', zIndex:200, display:'flex', alignItems:'center', justifyContent:'center', padding:'20px' }} onClick={() => { setSelected(null); resetForm(); }}>
          <div style={{ background:'white', borderRadius:'20px', width:'100%', maxWidth:'560px', boxShadow:'0 40px 80px rgba(0,0,0,0.3)', maxHeight:'90vh', overflowY:'auto' }} onClick={e => e.stopPropagation()}>

            {/* Modal Header */}
            <div style={{ padding:'24px 28px', borderBottom:'1px solid #f1f5f9', display:'flex', justifyContent:'space-between', alignItems:'center', position:'sticky', top:0, background:'white', borderRadius:'20px 20px 0 0', zIndex:1 }}>
              <div>
                <h3 style={{ color:'#1e293b', fontSize:'17px', fontWeight:'800', margin:'0 0 3px' }}>Submit Candidate</h3>
                <p style={{ color:'#94a3b8', fontSize:'12px', margin:0 }}>For: <strong style={{ color:'#6366f1' }}>{selected.title}</strong></p>
              </div>
              <div style={{ display:'flex', alignItems:'center', gap:'12px' }}>
                {/* Step indicator */}
                <div style={{ display:'flex', gap:'6px' }}>
                  {[1,2].map(s => (
                    <div key={s} style={{ width:'24px', height:'24px', borderRadius:'50%', display:'flex', alignItems:'center', justifyContent:'center', fontSize:'11px', fontWeight:'700',
                      background: step >= s ? 'linear-gradient(135deg,#6366f1,#8b5cf6)' : '#f1f5f9',
                      color: step >= s ? 'white' : '#94a3b8' }}>{s}</div>
                  ))}
                </div>
                <button onClick={() => { setSelected(null); resetForm(); }} style={{ background:'none', border:'none', cursor:'pointer', color:'#94a3b8' }}><X size={20}/></button>
              </div>
            </div>

            <div style={{ padding:'28px' }}>

              {/* STEP 1 — CV Upload */}
              {step === 1 && (
                <div>
                  <div style={{ textAlign:'center', marginBottom:'24px' }}>
                    <div style={{ fontSize:'48px', marginBottom:'12px' }}>📄</div>
                    <h4 style={{ color:'#1e293b', fontSize:'16px', fontWeight:'700', margin:'0 0 6px' }}>Upload Candidate CV</h4>
                    <p style={{ color:'#64748b', fontSize:'13px', margin:0 }}>AI will automatically extract candidate details from the CV</p>
                  </div>

                  {/* Drag & Drop Zone */}
                  <label style={{ display:'block', border:'2px dashed #c4b5fd', borderRadius:'14px', padding:'36px 24px', textAlign:'center', cursor:'pointer', background:'#faf5ff', transition:'all 0.2s' }}
                    onDragOver={e => { e.preventDefault(); e.currentTarget.style.borderColor = '#6366f1'; e.currentTarget.style.background = '#f5f3ff'; }}
                    onDragLeave={e => { e.currentTarget.style.borderColor = '#c4b5fd'; e.currentTarget.style.background = '#faf5ff'; }}
                    onDrop={e => { e.preventDefault(); e.currentTarget.style.borderColor = '#c4b5fd'; const f = e.dataTransfer.files[0]; if(f) handleCvUpload(f); }}>
                    <input type="file" accept=".pdf" style={{ display:'none' }} onChange={e => { if(e.target.files[0]) handleCvUpload(e.target.files[0]); }} />
                    {cvParsing ? (
                      <div>
                        <div style={{ width:'40px', height:'40px', border:'3px solid #ede9fe', borderTop:'3px solid #6366f1', borderRadius:'50%', animation:'spin 1s linear infinite', margin:'0 auto 12px' }} />
                        <p style={{ color:'#6366f1', fontSize:'14px', fontWeight:'600', margin:0 }}>🤖 AI is parsing CV...</p>
                        <style>{`@keyframes spin{to{transform:rotate(360deg)}}`}</style>
                      </div>
                    ) : (
                      <div>
                        <Upload size={36} color="#8b5cf6" style={{ margin:'0 auto 12px', display:'block' }} />
                        <p style={{ color:'#6366f1', fontSize:'14px', fontWeight:'700', margin:'0 0 4px' }}>Click or drag CV here</p>
                        <p style={{ color:'#94a3b8', fontSize:'12px', margin:0 }}>PDF format • Max 5MB</p>
                      </div>
                    )}
                  </label>

                  <button onClick={() => setStep(2)} style={{ width:'100%', marginTop:'16px', padding:'11px', background:'#f8fafc', color:'#64748b', border:'1px solid #e2e8f0', borderRadius:'8px', cursor:'pointer', fontSize:'13px', fontFamily:'inherit', fontWeight:'600' }}>
                    Skip — Fill Manually →
                  </button>
                </div>
              )}

              {/* STEP 2 — Verify Form */}
              {step === 2 && (
                <div>
                  {cvParsed && (
                    <div style={{ background:'#f0fdf4', border:'1px solid #86efac', borderRadius:'10px', padding:'12px 16px', marginBottom:'20px', display:'flex', alignItems:'center', gap:'10px' }}>
                      <span style={{ fontSize:'20px' }}>✅</span>
                      <div>
                        <div style={{ color:'#166534', fontSize:'13px', fontWeight:'700' }}>CV Parsed Successfully!</div>
                        <div style={{ color:'#16a34a', fontSize:'12px' }}>Please verify and fill missing fields below</div>
                      </div>
                    </div>
                  )}

                  {/* Interview Status Badge */}
                  {interviewStatus && (
                    <div style={{ marginBottom:'16px', padding:'12px 16px', borderRadius:'10px', display:'flex', alignItems:'center', gap:'10px',
                      background: interviewStatus==='checking' ? '#f8fafc' : interviewStatus==='done' ? '#f0fdf4' : '#fffbeb',
                      border: `1px solid ${interviewStatus==='checking'?'#e2e8f0':interviewStatus==='done'?'#86efac':'#fde68a'}` }}>
                      <span style={{ fontSize:'18px' }}>{interviewStatus==='checking'?'⏳':interviewStatus==='done'?'✅':'📋'}</span>
                      <div>
                        <div style={{ fontSize:'13px', fontWeight:'700', color: interviewStatus==='done'?'#166534':'#92400e' }}>
                          AI Interview: {interviewStatus==='checking'?'Checking...':interviewStatus==='done'?'Already Completed':'Pending'}
                        </div>
                        <div style={{ fontSize:'11px', color:'#64748b' }}>
                          {interviewStatus==='done'?'Score available in system':'Interview link will be sent after admin approval'}
                        </div>
                      </div>
                    </div>
                  )}

                  <div style={{ display:'grid', gap:'14px' }}>
                    <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:'12px' }}>
                      <div>
                        <label style={{ color:'#64748b', fontSize:'11px', fontWeight:'600', display:'block', marginBottom:'6px' }}>FULL NAME *</label>
                        <input value={form.name} onChange={e=>setForm({...form,name:e.target.value})} placeholder="Candidate Name" style={inp} />
                      </div>
                      <div>
                        <label style={{ color:'#64748b', fontSize:'11px', fontWeight:'600', display:'block', marginBottom:'6px' }}>EMAIL</label>
                        <input value={form.email} onChange={e=>setForm({...form,email:e.target.value})} placeholder="email@example.com" style={inp} />
                      </div>
                    </div>
                    <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:'12px' }}>
                      <div>
                        <label style={{ color:'#64748b', fontSize:'11px', fontWeight:'600', display:'block', marginBottom:'6px' }}>PHONE</label>
                        <input value={form.phone} onChange={e=>setForm({...form,phone:e.target.value})} placeholder="9876543210" style={inp} />
                      </div>
                      <div>
                        <label style={{ color:'#64748b', fontSize:'11px', fontWeight:'600', display:'block', marginBottom:'6px' }}>EXPERIENCE (yrs) *</label>
                        <input type="number" value={form.experience} onChange={e=>setForm({...form,experience:e.target.value})} placeholder="3" style={inp} />
                      </div>
                    </div>
                    <div>
                      <label style={{ color:'#64748b', fontSize:'11px', fontWeight:'600', display:'block', marginBottom:'6px' }}>SKILLS</label>
                      <input value={form.skills} onChange={e=>setForm({...form,skills:e.target.value})} placeholder="React, Node.js, MongoDB" style={inp} />
                      {cvParsed && form.skills && (
                        <div style={{ display:'flex', gap:'4px', flexWrap:'wrap', marginTop:'8px' }}>
                          {form.skills.split(',').map(s=>s.trim()).filter(Boolean).map(s => (
                            <span key={s} style={{ padding:'2px 8px', background:'#ede9fe', color:'#6366f1', borderRadius:'4px', fontSize:'11px' }}>{s}</span>
                          ))}
                        </div>
                      )}
                    </div>
                    <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:'12px' }}>
                      <div>
                        <label style={{ color:'#64748b', fontSize:'11px', fontWeight:'600', display:'block', marginBottom:'6px' }}>RATE TYPE *</label>
                        <select value={form.rateType} onChange={e=>setForm({...form,rateType:e.target.value,vendorRate:''})} style={{...inp,cursor:'pointer'}}>
                          <option value="same_budget">Same Budget (Rs.{selected?.budget?.toLocaleString()})</option>
                          <option value="negotiate">Negotiate</option>
                          <option value="lower_budget">Lower Budget</option>
                        </select>
                        {form.rateType === 'negotiate' && (
                          <div style={{marginTop:'8px'}}>
                            <input type="number" value={form.vendorRate} onChange={e=>setForm({...form,vendorRate:e.target.value})} placeholder="Enter your rate" style={inp}/>
                            <p style={{color:'#f59e0b',fontSize:'11px',margin:'4px 0 0'}}>Admin may counter this rate</p>
                          </div>
                        )}
                        {form.rateType === 'lower_budget' && (
                          <div style={{marginTop:'8px'}}>
                            <input type="number" value={form.vendorRate} onChange={e=>{ const v=Number(e.target.value); if(v<=selected?.budget) setForm({...form,vendorRate:e.target.value}); }} placeholder={`Max Rs.${selected?.budget?.toLocaleString()}`} max={selected?.budget} style={inp}/>
                            <p style={{color:'#16a34a',fontSize:'11px',margin:'4px 0 0'}}>Max: Rs.{selected?.budget?.toLocaleString()}</p>
                          </div>
                        )}
                      </div>
                      <div>
                        <label style={{ color:'#64748b', fontSize:'11px', fontWeight:'600', display:'block', marginBottom:'6px' }}>NOTICE PERIOD *</label>
                        <select value={form.noticePeriod} onChange={e=>setForm({...form,noticePeriod:e.target.value})} style={{ ...inp, cursor:'pointer' }}>
                          <option value="">Select</option>
                          <option value="Immediate">Immediate</option>
                          <option value="15 days">15 Days</option>
                          <option value="30 days">30 Days</option>
                          <option value="45 days">45 Days</option>
                          <option value="60 days">60 Days</option>
                          <option value="90 days">90 Days</option>
                        </select>
                      </div>
                    </div>
                    <div>
                      <label style={{ color:'#64748b', fontSize:'11px', fontWeight:'600', display:'block', marginBottom:'6px' }}>AVAILABILITY</label>
                      <select value={form.availability} onChange={e=>setForm({...form,availability:e.target.value})} style={{ ...inp, cursor:'pointer' }}>
                        <option value="immediate">Immediate</option>
                        <option value="15days">15 Days</option>
                        <option value="30days">30 Days</option>
                        <option value="60days">60 Days</option>
                      </select>
                    </div>
                    <div>
                      <label style={{ color:'#64748b', fontSize:'11px', fontWeight:'600', display:'block', marginBottom:'6px' }}>SHORT SUMMARY *</label>
                      <textarea value={form.summary} onChange={e=>setForm({...form,summary:e.target.value})}
                        placeholder="Brief about candidate — skills, experience, strengths..."
                        style={{ ...inp, minHeight:'80px', resize:'vertical' }} />
                    </div>

                    {cvFile && (
                      <div style={{ background:'#f5f3ff', border:'1px solid #c4b5fd', borderRadius:'8px', padding:'10px 14px', display:'flex', alignItems:'center', gap:'10px' }}>
                        <FileText size={16} color="#6366f1" />
                        <span style={{ color:'#6366f1', fontSize:'13px', fontWeight:'600', flex:1 }}>{cvFile.name}</span>
                        <button onClick={() => { setCvFile(null); setCvParsed(false); }} style={{ background:'none', border:'none', cursor:'pointer', color:'#94a3b8' }}><X size={14}/></button>
                      </div>
                    )}
                  </div>

                  <div style={{ display:'grid', gridTemplateColumns:'1fr 2fr', gap:'10px', marginTop:'20px' }}>
                    <button onClick={() => setStep(1)} style={{ padding:'11px', background:'#f8fafc', color:'#64748b', border:'1px solid #e2e8f0', borderRadius:'8px', cursor:'pointer', fontFamily:'inherit', fontWeight:'600', fontSize:'13px' }}>
                      ← Back
                    </button>
                    <button onClick={submitCandidate}
                      disabled={loading || !form.name || !form.experience || (form.rateType !== 'same_budget' && !form.vendorRate)}
                      style={{ padding:'12px', background: (!form.name||!form.experience||(form.rateType!=='same_budget'&&!form.vendorRate)) ? '#f1f5f9' : 'linear-gradient(135deg,#6366f1,#8b5cf6)',
                        color: (!form.name||!form.experience||(form.rateType!=='same_budget'&&!form.vendorRate)) ? '#94a3b8' : 'white',
                        border:'none', borderRadius:'8px', cursor: loading?'wait':'pointer', fontSize:'14px', fontWeight:'700', fontFamily:'inherit' }}>
                      {loading ? '⏳ Submitting...' : '✅ Submit Candidate'}
                    </button>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>

    {showBenchModal && (
      <div style={{position:'fixed',inset:0,background:'rgba(0,0,0,0.5)',zIndex:300,display:'flex',alignItems:'center',justifyContent:'center',padding:'20px'}}>
        <div style={{background:'white',borderRadius:'16px',width:'100%',maxWidth:'520px',maxHeight:'80vh',display:'flex',flexDirection:'column',overflow:'hidden'}}>
          <div style={{padding:'20px 24px',borderBottom:'1px solid #f1f5f9',display:'flex',justifyContent:'space-between',alignItems:'center'}}>
            <div>
              <h3 style={{margin:0,fontSize:'16px',fontWeight:'700',color:'#0f172a'}}>Submit from Bench</h3>
              <p style={{margin:'4px 0 0',fontSize:'12px',color:'#64748b'}}>Select up to 3 candidates for <strong>{benchJob?.title}</strong></p>
            </div>
            <button onClick={() => setShowBenchModal(false)} style={{background:'none',border:'none',cursor:'pointer',color:'#94a3b8',fontSize:'20px'}}>✕</button>
          </div>
          <div style={{overflowY:'auto',flex:1,padding:'16px 24px'}}>
            {benchList.length === 0 ? (
              <div style={{textAlign:'center',padding:'40px',color:'#94a3b8'}}><p>No bench candidates available.</p></div>
            ) : benchList.map(b => (
              <div key={b._id} onClick={() => toggleBenchSelect(b._id)}
                style={{display:'flex',gap:'12px',alignItems:'center',padding:'12px',borderRadius:'10px',border:`2px solid ${selectedBench.includes(b._id)?'#6366f1':'#e2e8f0'}`,marginBottom:'10px',cursor:'pointer',background:selectedBench.includes(b._id)?'#f0f0ff':'white',transition:'all 0.15s'}}>
                <div style={{width:'40px',height:'40px',borderRadius:'10px',background:'#6366f1',display:'flex',alignItems:'center',justifyContent:'center',color:'white',fontWeight:'700',fontSize:'16px',flexShrink:0}}>
                  {b.name?.charAt(0)?.toUpperCase()}
                </div>
                <div style={{flex:1}}>
                  <div style={{fontWeight:'600',fontSize:'14px',color:'#0f172a'}}>{b.isAnonymous?'Anonymous':b.name}</div>
                  <div style={{fontSize:'12px',color:'#6366f1'}}>{b.title}</div>
                  <div style={{fontSize:'11px',color:'#94a3b8',marginTop:'2px'}}>{b.experience} yrs · ₹{b.rate?.toLocaleString()}/{b.rateType}</div>
                </div>
                {selectedBench.includes(b._id) && <span style={{color:'#6366f1',fontSize:'18px',fontWeight:'700'}}>✓</span>}
              </div>
            ))}
          </div>
          <div style={{padding:'16px 24px',borderTop:'1px solid #f1f5f9',display:'flex',gap:'10px',alignItems:'center'}}>
            <span style={{fontSize:'12px',color:'#94a3b8',flex:1}}>{selectedBench.length}/3 selected</span>
            <button onClick={() => setShowBenchModal(false)} style={{padding:'10px 20px',background:'white',color:'#64748b',border:'1px solid #e2e8f0',borderRadius:'8px',cursor:'pointer',fontSize:'13px',fontFamily:'inherit'}}>Cancel</button>
            <button onClick={submitFromBench} disabled={!selectedBench.length || submittingBench}
              style={{padding:'10px 20px',background:selectedBench.length?'linear-gradient(135deg,#6366f1,#8b5cf6)':'#e2e8f0',color:selectedBench.length?'white':'#94a3b8',border:'none',borderRadius:'8px',cursor:selectedBench.length?'pointer':'not-allowed',fontSize:'13px',fontWeight:'600',fontFamily:'inherit'}}>
              {submittingBench?'Submitting...':'Submit Selected'}
            </button>
          </div>
        </div>
      </div>
    )}
    </>
  );
}
